import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Box, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCart } from '../contexts/CartContext';
import { useSession } from '../contexts/SessionContext';
import PageLayout from '../components/common/PageLayout';

const ConfirmationPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { cart, clearCart, setCart } = useCart();
  const { session, resetSession } = useSession();
  
  // Get order data from state or session
  const order = React.useMemo(() => {
    // Prefer location state, but backfill missing fields from session.orderDetails
    if (state?.order || state) {
      const base = state.order || state;
      const fallback = session?.orderDetails || {};
      return {
        ...base,
        payment_method_title: base.payment_method_title || fallback.payment_method_title || 'Credit Card',
        line_items: base.line_items || fallback.line_items || []
      };
    }

    // Otherwise, use session.orderDetails
    if (session?.orderDetails) {
      return {
        ...session.orderDetails,
        payment_method_title: session.orderDetails.payment_method_title || 'Credit Card',
        line_items: session.orderDetails.line_items || []
      };
    }
    
    return null;
  }, [state, session]);

  const hasSplitPledge = React.useMemo(
    () =>
      order &&
      ((Array.isArray(order.payment_order_ids) && order.payment_order_ids.length > 0) ||
        (Array.isArray(order.pledge_order_ids) && order.pledge_order_ids.length > 0)),
    [order]
  );
  
  // Get customer from session (either root or nested in orderDetails)
  const customer = React.useMemo(() => ({
    ...(session?.customer || {}),
    ...(session?.orderDetails?.customer || {})
  }), [session]);
  
  // Get billing from orderDetails or fallback to customer info
  const billing = React.useMemo(() => ({
    ...(session?.orderDetails?.billing || {
      first_name: customer?.first_name,
      last_name: customer?.last_name,
      email: customer?.email,
      phone: customer?.phone
    })
  }), [session, customer]);

  // Track if component is mounted
  const mountedRef = React.useRef(false);

  // Log order and customer details for debugging
  useEffect(() => {
    if (order && !mountedRef.current) {
      mountedRef.current = true; // Set the flag to true after first render
      
      console.log('=== SESSION STRUCTURE ===');
      console.log('Session:', JSON.parse(JSON.stringify(session || {}, null, 2)));
      
      console.log('\n=== ORDER DETAILS ===');
      console.table({
        'Order ID': order.id || 'N/A',
        'Order Number': order.number || order.id || 'N/A',
        'Status': order.status || 'N/A',
        'Total': order.total ? `$${parseFloat(order.total).toFixed(2)}` : 'N/A',
        'Payment Method': order.payment_method_title || 'N/A',
        'Date': order.date_created ? new Date(order.date_created).toLocaleString() : 'N/A'
      });
      
      console.log('\n=== CUSTOMER DETAILS ===');
      console.table({
        'Name': customer?.name || 
               (customer?.first_name && customer?.last_name ? 
                `${customer.first_name} ${customer.last_name}` : 'N/A'),
        'Email': customer?.email || 'N/A',
        'Phone': customer?.phone || 'N/A',
        'User ID': customer?.wp_user_id || customer?.user_id || 'N/A'
      });
      
      console.log('\n=== BILLING DETAILS ===');
      console.table({
        'Name': (billing?.first_name && billing?.last_name) ? 
                `${billing.first_name} ${billing.last_name}` : 'N/A',
        'Email': billing?.email || 'N/A',
        'Phone': billing?.phone || 'N/A',
        'Address': [
          billing?.address_1,
          billing?.address_2,
          billing?.city,
          billing?.state,
          billing?.postcode,
          billing?.country
        ].filter(Boolean).join(', ') || 'N/A'
      });
      
      if (order.line_items?.length > 0) {
        console.log('\n=== ORDER ITEMS ===');
        console.table(order.line_items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.total
        })));
      }
      
      // Log email receipt status
      const emailTo = billing?.email || customer?.email;
      if (emailTo) {
        console.log('\n=== EMAIL RECEIPT ===');
        console.log(`Email receipt should be sent to: ${emailTo}`);
        if (order.email_sent) {
          console.log('Email receipt was successfully sent');
        } else {
          console.log('Email receipt may not have been sent (email_sent flag is false)');
        }
      }
    }
  }, [order, session, customer, billing]);

  // Clear session data when component unmounts or when returning home
  const clearAllSessionData = useCallback(() => {
    // Clear cart
    clearCart();
    
    // Clear session data
    resetSession({ skipNavigation: true });

    // Clear any other session storage items
    sessionStorage.clear();
    localStorage.removeItem('cart');
    localStorage.removeItem('user');
    localStorage.removeItem('order');
    localStorage.removeItem('paymentStatus');
    
    // Clear any URL parameters
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [clearCart, resetSession]);

  // Handle return home button click
  const handleReturnHome = useCallback(async () => {
    try {
      // Clear cart state first
      clearCart();
      
      // Clear all local storage related to cart and order
      localStorage.removeItem('cart');
      localStorage.removeItem('order');
      localStorage.removeItem('paymentStatus');
      
      // Clear session data
      await clearAllSessionData();
      
      // Force clear the cart state again to ensure it's cleared
      // This helps with any race conditions
      setTimeout(() => {
        clearCart();
        localStorage.removeItem('cart');
        
        // Clear any cart-related state that might be in session storage
        sessionStorage.removeItem('cart');
        
        // Force a state update to ensure UI reflects the empty cart
        if (setCart) {
          setCart([]);
        }
      }, 50);
      
      // Navigate to home after a small delay to ensure state is cleared
      setTimeout(() => {
        navigate('/home', { 
          replace: true,
          state: { cartCleared: true } // Pass a flag to indicate cart was cleared
        });
      }, 100);
    } catch (error) {
      console.error('Error clearing session data:', error);
      // Clear local storage as fallback
      localStorage.removeItem('cart');
      localStorage.removeItem('order');
      
      // Still navigate home even if there was an error
      navigate('/home', { 
        replace: true,
        state: { cartCleared: true }
      });
    }
  }, [clearAllSessionData, clearCart, navigate]);

  // Auto-redirect to Home after 30 seconds (no warning dialog)
  useEffect(() => {
    const timer = setTimeout(() => {
      handleReturnHome();
    }, 30000);
    return () => clearTimeout(timer);
  }, [handleReturnHome]);

  if (!order) {
    return (
      <PageLayout contentMaxWidth="md">
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            No order information found
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReturnHome}
            startIcon={<HomeIcon />}
            size="large"
            sx={{ 
              minWidth: 240,
              py: 1.5,
              fontSize: '1.1rem',
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: 3
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Return to Home
          </Button>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout contentMaxWidth="md">
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: '#e0e0e0', boxShadow: '0 8px 20px rgba(0,0,0,0.08)', bgcolor: 'rgba(76, 175, 80, 0.06)' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ mb: 4 }}>
          <CheckCircleOutlineIcon 
            color="success" 
            sx={{ fontSize: 80, mb: 2 }} 
          />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 800 }}>
            Thank you for your donation!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Your payment was successful and has been processed.
          </Typography>
          {billing?.email && 
           billing.email !== process.env.REACT_APP_GUEST_CHECKOUT_EMAIL && (
            <Typography variant="body1" sx={{ mt: 2, mb: 4, color: 'primary.main' }}>
              A receipt has been sent to {billing.email}.
            </Typography>
          )}

          <Typography variant="body1" sx={{ mt: 1, fontWeight: 900, color: 'success.main' }}>
            We appreciate your support. Your donation helps us continue our mission.
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <img
              src={process.env.PUBLIC_URL + '/namaste.jpg'}
              alt="Namaste"
              style={{ maxWidth: 160, width: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 4, textAlign: 'center', fontWeight: 700 }}>Donation Details</Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={5} sx={{ textAlign: 'center' }}>
              <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderRight: { sm: '1px solid', borderColor: 'divider' } }}>
                {order.id && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Order Number</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>{order.id}</Typography>
                  </Box>
                )}

                {hasSplitPledge && (
                  <Box sx={{ mb: 2, textAlign: 'left', maxWidth: 420, mx: 'auto' }}>
                    {Array.isArray(order.payment_order_ids) && order.payment_order_ids.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        Payment orders (receipts): {order.payment_order_ids.join(', ')}
                      </Typography>
                    )}
                    {Array.isArray(order.pledge_order_ids) && order.pledge_order_ids.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Pledge records (full amount, no charge on these): {order.pledge_order_ids.join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {(customer?.name || (billing?.first_name && billing?.last_name)) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Devotee Name</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {customer?.name || `${billing.first_name} ${billing.last_name}`.trim()}
                    </Typography>
                  </Box>
                )}
                
                {billing?.email && billing.email !== process.env.REACT_APP_GUEST_CHECKOUT_EMAIL && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" sx={{ mt: 1, wordBreak: 'break-word' }}>
                      {billing.email}
                    </Typography>
                  </Box>
                )}
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {order.date_created && new Date(order.date_created).getFullYear() > 1970 ? 
                      new Date(order.date_created).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      }) : 
                      new Date().toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                    }
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={5} sx={{ textAlign: 'center' }}>
              <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {order.payment_method_title || 'Credit Card'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h5" sx={{ mt: 1, color: 'primary.main', fontWeight: 'bold' }}>
                    ${parseFloat(order.total || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {order.line_items?.length > 0 && (
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                Donation Items
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.line_items.map((item, index) => {
                      const qty = Number(item.quantity || 1);
                      const price = Number(item.price || 0);
                      const rowTotal = parseFloat(String(item.total || item.subtotal || '0'), 10) || (price * qty);
                      return (
                        <TableRow key={index}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="center">{qty}</TableCell>
                          <TableCell align="right">${price.toFixed(2)}</TableCell>
                          <TableCell align="right">${rowTotal.toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {Array.isArray(order.fee_lines) && order.fee_lines.length > 0 && order.fee_lines.map((fee, idx) => (
                      <TableRow key={`feel-${idx}`}>
                        <TableCell colSpan={3} align="right" sx={{ color: 'text.secondary' }}>
                          {fee.name || 'Fee'}
                        </TableCell>
                        <TableCell align="right">${parseFloat(fee.total || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {Number(order.fee_amount) > 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="right" sx={{ color: 'text.secondary' }}>
                          {order.fee_name || 'Processing Fee'}
                        </TableCell>
                        <TableCell align="right">${Number(order.fee_amount).toFixed(2)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>${parseFloat(order.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleReturnHome}
            startIcon={<HomeIcon />}
            sx={{ 
              mt: 3,
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              minWidth: 240,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Return to Home
          </Button>
        </Box>
      </Box>
      </Paper>
    </PageLayout>
  );
};

export default ConfirmationPage;
